/** AI 智慧選課三步流程。自 public/js/ai-advisor.js 移植。
 *
 *  步驟1 條件設定(滑桿/時間禁區網格/卡片/標籤/開關/自由輸入)
 *  步驟2 導師對話(意圖收斂 → 結構化 JSON 摘要卡)
 *  步驟3 硬過濾排課(週曆課表渲染、一鍵採納、微調彈窗、3 次上限防呆)
 *
 *  硬性條件的過濾與驗證都在後端(規格書的雙軌引擎),前端只負責收集設定與呈現。
 */
import { request } from './api';
import type {
  AdvisorContext, AdvisorIntent, AdvisorSettings, PlanCourse, PlanResult,
} from './types';

const WD = ['', '週一', '週二', '週三', '週四', '週五', '週六', '週日'];

/** 與後端 ai_service/services/advisor.py 的 BANDS 對齊 */
const BANDS = [
  { label: '早八', start: '08:00', end: '10:00' },
  { label: '上午', start: '10:00', end: '12:00' },
  { label: '午後', start: '13:00', end: '15:00' },
  { label: '傍晚', start: '15:00', end: '17:00' },
  { label: '夜間', start: '18:00', end: '20:00' },
];

interface Choice { v: string; label?: string; d?: string }

const CAREERS: Choice[] = [
  { v: '直接就業', d: '以就職即戰力為主' },
  { v: '考研推甄', d: '衝刺升學與研究基礎' },
  { v: '出國留學', d: '強化語言與國際視野' },
  { v: '探索階段', d: '廣泛嘗試找方向' },
];
const DOMAINS = ['軟體工程', '數據 AI', '數位行銷', '設計產品'];
const ASSESS = ['偏向考試', '偏向專案', '兩者平衡'];
const STYLES = ['傳統講授', '實作演練', '團體討論', '個案分析'];
const ATTITUDES: Choice[] = [
  { v: '甜涼高分', d: '評分友善、負擔輕' },
  { v: '硬核扎實', d: '重實力、不怕操' },
  { v: '攻守兼備', d: '難易搭配平衡' },
];
const AVOIDS = ['避重度數學', '避全英文', '避簡報', '避分組'];
const EXPANSIONS: Choice[] = [
  { v: 'own', label: '本系優先', d: '只排本系課程' },
  { v: 'cross', label: '開放跨系', d: '全校課程都可排' },
  { v: 'general', label: '補足通識', d: '本系＋通識選修' },
];
const REFINE_REASONS = [
  { v: 'time', label: '時段安排不理想' },
  { v: 'hard', label: '課程難度不合適' },
  { v: 'interest', label: '課程內容與興趣不符' },
  { v: 'credits', label: '學分組合想調整' },
  { v: 'direction', label: '整體方向不對(回導師對話重新收斂)' },
];
const COLORS = ['ai__timetable-event--blue', 'ai__timetable-event--orange'];
const STORE_KEY = 'ekm-ai-advisor-settings-v1';

interface AdvisorState {
  ctx: AdvisorContext | null;
  settings: AdvisorSettings;
  history: { role: 'user' | 'assistant'; content: string }[];
  intent: AdvisorIntent | null;
  intentDone: boolean;
  plan: PlanResult | null;
  blacklist: number[];
  retriesLeft: number;
  /** 「方向錯誤」微調:回對話重新收斂後,下一次排課仍計入微調次數 */
  refineAfterChat: boolean;
  chatStarted: boolean;
  sending: boolean;
  adopted: boolean;
}

const S: AdvisorState = {
  ctx: null,
  settings: {
    creditsMin: 15, creditsMax: 20, bannedSlots: [],
    career: '探索階段', domains: [], assessment: '兩者平衡', teachingStyles: [],
    attitude: '攻守兼備', avoid: [], weakReinforce: [], expansion: 'cross', freeText: '',
  },
  history: [], intent: null, intentDone: false,
  plan: null, blacklist: [], retriesLeft: 3,
  refineAfterChat: false, chatStarted: false, sending: false, adopted: false,
};

const esc = (s: unknown) =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');

const $ = (id: string): HTMLElement => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`頁面缺少必要元素 #${id}`);
  return el;
};

function saveSettings(): void {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(S.settings));
  } catch { /* 隱私模式忽略 */ }
}

function loadSettings(): void {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (raw) Object.assign(S.settings, JSON.parse(raw));
  } catch { /* 壞資料忽略 */ }
}

// ── 步驟切換 ──────────────────────────────────────────
function gotoStep(n: number): void {
  [1, 2, 3].forEach((i) => {
    $(`ai-step-${i}`).hidden = i !== n;
  });
  document.querySelectorAll<HTMLElement>('.ai-steps__item').forEach((el) => {
    const step = Number(el.dataset.step);
    el.classList.toggle('is-active', step === n);
    el.classList.toggle('is-done', step < n);
  });
  window.scrollTo(0, 0);
}

// ══ 步驟 1:條件設定表單 ═══════════════════════════════
function renderSlider(): void {
  const min = $('ai-credit-min') as HTMLInputElement;
  const max = $('ai-credit-max') as HTMLInputElement;
  min.value = String(S.settings.creditsMin);
  max.value = String(S.settings.creditsMax);
  const lo = Number(min.min);
  const span = Number(min.max) - lo;
  const p1 = ((S.settings.creditsMin - lo) / span) * 100;
  const p2 = ((S.settings.creditsMax - lo) / span) * 100;
  $('ai-slider-fill').style.left = `${p1}%`;
  $('ai-slider-fill').style.width = `${p2 - p1}%`;
  $('ai-credit-label').textContent =
    `${S.settings.creditsMin} ~ ${S.settings.creditsMax} 學分`;
}

function bindSlider(): void {
  const min = $('ai-credit-min') as HTMLInputElement;
  const max = $('ai-credit-max') as HTMLInputElement;
  min.addEventListener('input', () => {
    S.settings.creditsMin = Math.min(Number(min.value), S.settings.creditsMax);
    renderSlider(); syncSummary(); saveSettings();
  });
  max.addEventListener('input', () => {
    S.settings.creditsMax = Math.max(Number(max.value), S.settings.creditsMin);
    renderSlider(); syncSummary(); saveSettings();
  });
}

// 時間禁區網格:5 時段帶 × 週一~週五,格子點選切換;快捷鍵一鍵排除
const zoneKey = (d: number, b: number) => `${d}-${b}`;

function renderZoneGrid(): void {
  const banned = S.settings.bannedSlots;
  let html = '<div class="ai-zonegrid__head">時段</div>';
  for (let d = 1; d <= 5; d += 1) html += `<div class="ai-zonegrid__head">${WD[d]}</div>`;
  BANDS.forEach((b, bi) => {
    html += `<div class="ai-zonegrid__label">${b.label}<span>${b.start}-${b.end}</span></div>`;
    for (let day = 1; day <= 5; day += 1) {
      const k = zoneKey(day, bi);
      const on = banned.includes(k);
      html += `<button type="button" class="ai-zonegrid__cell${on ? ' is-banned' : ''}" `
        + `data-key="${k}">${on ? '✕ 禁區' : ''}</button>`;
    }
  });
  const host = $('ai-zone-grid');
  host.innerHTML = html;
  host.querySelectorAll<HTMLElement>('.ai-zonegrid__cell').forEach((c) => {
    c.addEventListener('click', () => {
      const k = c.dataset.key ?? '';
      const i = S.settings.bannedSlots.indexOf(k);
      if (i >= 0) S.settings.bannedSlots.splice(i, 1);
      else S.settings.bannedSlots.push(k);
      renderZoneGrid(); renderZoneChips(); syncSummary(); saveSettings();
    });
  });
}

const ZONE_PRESETS = [
  { label: '一鍵排除早八', keys: [1, 2, 3, 4, 5].map((d) => zoneKey(d, 0)) },
  { label: '一鍵排除夜間課程', keys: [1, 2, 3, 4, 5].map((d) => zoneKey(d, 4)) },
  { label: '一鍵排除週五全天', keys: BANDS.map((_b, b) => zoneKey(5, b)) },
];

function renderZoneChips(): void {
  const host = $('ai-zone-chips');
  let html = ZONE_PRESETS.map((p, i) => {
    const active = p.keys.every((k) => S.settings.bannedSlots.includes(k));
    return `<button type="button" class="ai-chip${active ? ' is-active' : ''}" `
      + `data-preset="${i}">${p.label}</button>`;
  }).join('');
  html += '<button type="button" class="ai-chip ai-chip--clear" data-clear="1">清除全部禁區</button>';
  host.innerHTML = html;

  host.querySelectorAll<HTMLElement>('[data-preset]').forEach((c) => {
    c.addEventListener('click', () => {
      const p = ZONE_PRESETS[Number(c.dataset.preset)];
      if (!p) return;
      const allOn = p.keys.every((k) => S.settings.bannedSlots.includes(k));
      for (const k of p.keys) {
        const i = S.settings.bannedSlots.indexOf(k);
        if (allOn && i >= 0) S.settings.bannedSlots.splice(i, 1);
        if (!allOn && i < 0) S.settings.bannedSlots.push(k);
      }
      renderZoneGrid(); renderZoneChips(); syncSummary(); saveSettings();
    });
  });
  host.querySelector<HTMLElement>('[data-clear]')?.addEventListener('click', () => {
    S.settings.bannedSlots = [];
    renderZoneGrid(); renderZoneChips(); syncSummary(); saveSettings();
  });
}

/** 單選卡片群(畢業規劃/修課態度/知識擴充) */
function renderChoices(
  hostId: string, items: Choice[], get: () => string, set: (v: string) => void,
): void {
  const host = $(hostId);
  host.innerHTML = items.map((it) => {
    const selected = get() === it.v;
    return `<button type="button" class="ai-choice${selected ? ' is-selected' : ''}" `
      + `data-v="${esc(it.v)}">`
      + `<span class="ai-choice__radio">${selected ? '<span class="ai-choice__dot"></span>' : ''}</span>`
      + `<span class="ai-choice__text"><strong>${esc(it.label || it.v)}</strong>`
      + `<small>${esc(it.d ?? '')}</small></span></button>`;
  }).join('');
  host.querySelectorAll<HTMLElement>('.ai-choice').forEach((c) => {
    c.addEventListener('click', () => {
      set(c.dataset.v ?? '');
      renderChoices(hostId, items, get, set); syncSummary(); saveSettings();
    });
  });
}

/** 多選標籤群(目標領域/授課型態) */
function renderTags(hostId: string, values: string[], listRef: () => string[]): void {
  const host = $(hostId);
  host.innerHTML = values.map((v) => {
    const on = listRef().includes(v);
    return `<button type="button" class="ai-tag${on ? ' is-selected' : ''}" `
      + `data-v="${esc(v)}">${esc(v)}</button>`;
  }).join('');
  host.querySelectorAll<HTMLElement>('.ai-tag').forEach((t) => {
    t.addEventListener('click', () => {
      const list = listRef();
      const i = list.indexOf(t.dataset.v ?? '');
      if (i >= 0) list.splice(i, 1);
      else list.push(t.dataset.v ?? '');
      renderTags(hostId, values, listRef); syncSummary(); saveSettings();
    });
  });
}

/** 單選膠囊(評量方式) */
function renderPills(): void {
  const host = $('ai-assess');
  host.innerHTML = ASSESS.map((v) =>
    `<button type="button" class="ai-pill${S.settings.assessment === v ? ' is-selected' : ''}" `
    + `data-v="${v}">${v}</button>`).join('');
  host.querySelectorAll<HTMLElement>('.ai-pill').forEach((p) => {
    p.addEventListener('click', () => {
      S.settings.assessment = p.dataset.v ?? '';
      renderPills(); syncSummary(); saveSettings();
    });
  });
}

/** 開關選單(個人避坑) */
function renderSwitches(): void {
  const host = $('ai-avoid');
  host.innerHTML = AVOIDS.map((v) => {
    const on = S.settings.avoid.includes(v);
    return `<label class="ai-switch"><span>${v}</span>`
      + `<span class="ai-switch__track${on ? ' is-on' : ''}" data-v="${v}">`
      + '<span class="ai-switch__knob"></span></span></label>';
  }).join('');
  host.querySelectorAll<HTMLElement>('.ai-switch__track').forEach((t) => {
    t.addEventListener('click', () => {
      const i = S.settings.avoid.indexOf(t.dataset.v ?? '');
      if (i >= 0) S.settings.avoid.splice(i, 1);
      else S.settings.avoid.push(t.dataset.v ?? '');
      renderSwitches(); syncSummary(); saveSettings();
    });
  });
}

/** 弱項補強:由背景資料自動帶入並預設勾選 */
function renderWeakTags(): void {
  const weak = S.ctx?.weakAreas ?? [];
  if (!weak.length) {
    $('ai-weak').innerHTML =
      '<span class="ai-form__hint">目前作業成績未偵測到明顯弱項,可安心衝刺 💪</span>';
    return;
  }
  const cats = weak.map((w) => w.category);
  // 首次載入(未存過設定)時預設全勾
  if (!S.settings._weakInit) {
    S.settings.weakReinforce = [...cats];
    S.settings._weakInit = true;
  }
  const labels: Record<string, string> = {};
  for (const w of weak) labels[w.category] = `${w.category}(平均 ${w.avgScore} 分)`;

  const host = $('ai-weak');
  host.innerHTML = cats.map((v) => {
    const on = S.settings.weakReinforce.includes(v);
    return `<button type="button" class="ai-tag ai-tag--warn${on ? ' is-selected' : ''}" `
      + `data-v="${esc(v)}">${esc(labels[v])}</button>`;
  }).join('');
  host.querySelectorAll<HTMLElement>('.ai-tag').forEach((t) => {
    t.addEventListener('click', () => {
      const i = S.settings.weakReinforce.indexOf(t.dataset.v ?? '');
      if (i >= 0) S.settings.weakReinforce.splice(i, 1);
      else S.settings.weakReinforce.push(t.dataset.v ?? '');
      renderWeakTags(); syncSummary(); saveSettings();
    });
  });
}

function syncSummary(): void {
  const s = S.settings;
  const parts = [
    `${s.creditsMin}~${s.creditsMax} 學分`,
    s.bannedSlots.length ? `禁區 ${s.bannedSlots.length} 時段` : '無時間禁區',
    s.career, s.assessment, s.attitude,
    EXPANSIONS.find((e) => e.v === s.expansion)?.label,
  ];
  if (s.domains.length) parts.push(`領域:${s.domains.join('、')}`);
  $('ai-form-summary').textContent = parts.filter(Boolean).join(' · ');
}

function renderForm(): void {
  renderSlider();
  renderZoneGrid();
  renderZoneChips();
  renderChoices('ai-career', CAREERS,
    () => S.settings.career, (v) => { S.settings.career = v; });
  renderTags('ai-domains', DOMAINS, () => S.settings.domains);
  renderPills();
  renderTags('ai-styles', STYLES, () => S.settings.teachingStyles);
  renderChoices('ai-attitude', ATTITUDES,
    () => S.settings.attitude, (v) => { S.settings.attitude = v; });
  renderSwitches();
  renderWeakTags();
  renderChoices('ai-expansion', EXPANSIONS,
    () => S.settings.expansion, (v) => { S.settings.expansion = v; });

  const ft = $('ai-freetext') as HTMLTextAreaElement;
  ft.value = S.settings.freeText;
  ft.addEventListener('input', () => {
    S.settings.freeText = ft.value;
    saveSettings();
  });
  syncSummary();
}

// ══ 步驟 2:導師對話 ═══════════════════════════════════
function addBubble(role: 'user' | 'ai', html: string): HTMLElement {
  const box = $('ai-chat-messages');
  const div = document.createElement('div');
  div.className = `ai__chat-bubble ai__chat-bubble--${role === 'user' ? 'user' : 'ai'}`;
  div.innerHTML = html;
  box.appendChild(div);
  box.scrollTop = box.scrollHeight;
  return div;
}

function renderIntentCard(): void {
  const it = S.intent;
  const body = $('ai-intent-body');
  $('ai-intent-empty').hidden = !!it;
  body.hidden = !it;
  if (!it) return;
  const chips = (it.coreSkills ?? [])
    .map((s) => `<span class="ai-intent__chip">${esc(s)}</span>`).join('');
  body.innerHTML =
    `<div class="ai-intent__row"><span class="ai-intent__key">職涯目標</span><span>${esc(it.careerGoal)}</span></div>`
    + `<div class="ai-intent__row"><span class="ai-intent__key">核心技能</span><span class="ai-intent__chips">${chips}</span></div>`
    + `<div class="ai-intent__row"><span class="ai-intent__key">偏好風格</span><span>${esc(it.preferenceStyle)}</span></div>`
    + `<div class="ai-intent__row ai-intent__row--summary">${esc(it.summary)}</div>`
    + (S.intentDone
      ? '<div class="ai-intent__done">✓ 意圖已收斂,可開始排課</div>'
      : '<div class="ai-intent__pending">意圖收斂中…</div>');
  ($('ai-to-plan') as HTMLButtonElement).disabled = !S.intentDone;
}

/** 傳給後端前拿掉前端內部欄位(底線開頭) */
function cleanSettings(): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(S.settings).filter(([k]) => !k.startsWith('_')));
}

async function chatRequest(message: string): Promise<void> {
  const thinking = addBubble('ai', 'AI 導師思考中<span class="ai-dots">…</span>');
  const sendBtn = $('ai-send') as HTMLButtonElement;
  sendBtn.disabled = true;
  try {
    const r = await request<{ reply: string; done: boolean; intent: AdvisorIntent }>(
      '/api/ai/advisor/chat',
      { json: { message, history: S.history, settings: cleanSettings() } });
    thinking.innerHTML = esc(r.reply).replace(/\n/g, '<br>');
    if (message) S.history.push({ role: 'user', content: message });
    S.history.push({ role: 'assistant', content: r.reply });
    S.intent = r.intent || S.intent;
    S.intentDone = !!r.done;
    renderIntentCard();
  } catch (e) {
    const message2 = e instanceof Error ? e.message : String(e);
    thinking.innerHTML = `<span style="color:#D32F2F">${esc(message2)}</span>`;
  } finally {
    sendBtn.disabled = false;
    S.sending = false;
  }
}

async function startChat(): Promise<void> {
  if (S.chatStarted) return;
  S.chatStarted = true;
  S.history = [];
  S.intent = null;
  S.intentDone = false;
  renderIntentCard();
  $('ai-chat-messages').innerHTML = '';
  S.sending = true;
  await chatRequest(''); // 空訊息 = 請導師依表單設定開場
}

async function sendChat(): Promise<void> {
  const input = $('ai-input') as HTMLInputElement;
  const text = input.value.trim();
  if (!text || S.sending) return;
  S.sending = true;
  input.value = '';
  addBubble('user', esc(text));
  await chatRequest(text);
}

// ══ 步驟 3:排課、課表渲染、採納與微調 ══════════════════
const toMin = (t: string) => {
  const [h, m] = t.split(':');
  return Number(h) * 60 + Number(m);
};

function cellCourses(day: number, band: number): PlanCourse[] {
  const b = BANDS[band];
  if (!b || !S.plan) return [];
  const b0 = toMin(b.start);
  const b1 = toMin(b.end);
  return (S.plan.courses ?? []).filter((c) => (c.schedules ?? []).some(
    (s) => s.weekday === day && toMin(s.start) < b1 && toMin(s.end) > b0));
}

function renderPlanTimetable(): void {
  let html = '<div class="ai__timetable-head">時段</div>';
  for (let d = 1; d <= 5; d += 1) html += `<div class="ai__timetable-head">${WD[d]}</div>`;
  BANDS.forEach((b, bi) => {
    html += `<div class="ai__timetable-label">${b.label}<br>`
      + `<span style="font-size:12px;color:#8C8C8C">${b.start}-${b.end}</span></div>`;
    for (let day = 1; day <= 5; day += 1) {
      const evs = cellCourses(day, bi);
      html += `<div class="ai__timetable-cell">${evs.map((c, i) => {
        const sch = (c.schedules ?? []).find((s) => s.weekday === day);
        return `<div class="ai__timetable-event ${COLORS[(c.id + i) % 2]}">`
          + `<div class="ai__timetable-event__time">${esc(sch?.start)} - ${esc(sch?.end)}</div>`
          + `<div class="ai__timetable-event__title">${esc(c.name)}</div></div>`;
      }).join('')}</div>`;
    }
  });
  $('ai-plan-timetable').innerHTML = html;
}

function renderPlan(): void {
  const p = S.plan;
  if (!p) return;
  $('ai-plan-name').textContent = p.planName + (p.mock ? '(模擬)' : '');
  $('ai-plan-overview').textContent = p.overview;
  $('ai-plan-credits').textContent = `總學分:${p.totalCredits} 學分`
    + `(期望 ${S.settings.creditsMin}~${S.settings.creditsMax})`;
  $('ai-plan-count').textContent = String(p.courses.length);
  renderPlanTimetable();

  const warn = $('ai-plan-warnings');
  warn.hidden = !p.warnings?.length;
  if (p.warnings?.length) {
    warn.innerHTML = p.warnings.map((w) => `<div>⚠ ${esc(w)}</div>`).join('');
  }

  $('ai-plan-reasons').innerHTML = p.courses.map((c) => {
    const time = (c.schedules ?? [])
      .map((s) => `${WD[s.weekday]} ${s.start}~${s.end}`).join(' / ');
    const meta = [esc(c.department), `課號 ${esc(c.code)}`, esc(c.courseType)].join(' · ');
    return '<div class="ai__rec-card">'
      + `<div class="ai__rec-card-top"><span class="ai__rec-card-title">${esc(c.name)}</span>`
      + `<span class="badge badge--green">${c.credits}學分</span></div>`
      + `<p class="ai__rec-card-desc"><strong style="color:#6C61E1">推薦理由:</strong>${esc(c.reason)}</p>`
      + `<div class="ai__rec-card-meta"><span class="fac20">${meta}</span>`
      + `<span class="fac20">${esc(c.teacherName ?? '')}</span></div>`
      + '<hr class="ai__rec-card-divider">'
      + `<div class="ai__rec-card-footer"><span class="ai__rec-card-time">${esc(time)}</span></div>`
      + '</div>';
  }).join('');

  $('ai-plan-tries').textContent = `剩餘微調次數:${S.retriesLeft} 次`;
  ($('ai-refine') as HTMLButtonElement).disabled = S.retriesLeft <= 0;
  const adopt = $('ai-adopt') as HTMLButtonElement;
  adopt.disabled = false;
  adopt.textContent = '一鍵採納全部課程';
  S.adopted = false;

  $('ai-plan-loading').hidden = true;
  $('ai-plan-exhausted').hidden = true;
  $('ai-plan-view').hidden = false;
}

async function runPlan(opts: { refine?: boolean; refineNote?: string } = {}): Promise<void> {
  gotoStep(3);
  $('ai-plan-view').hidden = true;
  $('ai-plan-exhausted').hidden = true;
  $('ai-plan-loading').hidden = false;
  try {
    const r = await request<PlanResult>('/api/ai/advisor/plan', {
      json: {
        settings: cleanSettings(),
        intent: S.intentDone ? S.intent : null,
        blacklist: S.blacklist,
        refine: !!opts.refine,
        refineNote: opts.refineNote ?? '',
      },
    });
    if (r.exhausted) {
      S.retriesLeft = 0;
      $('ai-plan-loading').hidden = true;
      $('ai-plan-exhausted').hidden = false;
      return;
    }
    S.plan = r;
    S.retriesLeft = r.retriesLeft;
    renderPlan();
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    $('ai-plan-loading').hidden = true;
    $('ai-plan-view').hidden = false;
    $('ai-plan-name').textContent = '排課失敗';
    $('ai-plan-overview').innerHTML = `<span style="color:#D32F2F">${esc(message)}</span>`;
    $('ai-plan-credits').textContent = '';
    $('ai-plan-count').textContent = '0';
    $('ai-plan-timetable').innerHTML = '';
    $('ai-plan-reasons').innerHTML = '';
    $('ai-plan-tries').textContent = '';
    ($('ai-adopt') as HTMLButtonElement).disabled = true;
  }
}

/** 一鍵採納:全部寫入行事曆預排(僅檢視,非實際加退選) */
async function adoptPlan(): Promise<void> {
  if (!S.plan || S.adopted) return;
  const btn = $('ai-adopt') as HTMLButtonElement;
  btn.disabled = true;
  btn.textContent = '加入預排中…';
  try {
    for (const c of S.plan.courses) {
      await request('/api/preselections', { json: { courseId: c.id } });
    }
    S.adopted = true;
    btn.textContent = '✓ 已全部加入預排';
    // 行事曆頁是 index.html(原本連到不存在的 calendar.html,是死連結)
    $('ai-plan-tries').innerHTML = '課程已加入行事曆預排(僅檢視),'
      + '<a href="index.html" style="color:#6C61E1">前往行事曆查看 ›</a>';
  } catch (e) {
    btn.disabled = false;
    btn.textContent = '一鍵採納全部課程';
    window.alert(`加入預排失敗:${e instanceof Error ? e.message : String(e)}`);
  }
}

// ── 微調彈窗 ──────────────────────────────────────────
let refineReason: string | null = null;

function openRefineModal(): void {
  if (!S.plan) return; // 排課失敗時無課表可微調
  refineReason = null;
  ($('ai-refine-note') as HTMLTextAreaElement).value = '';

  $('ai-refine-reasons').innerHTML = REFINE_REASONS.map((r) =>
    `<button type="button" class="ai-choice ai-choice--slim" data-v="${r.v}">`
    + `<span class="ai-choice__radio"></span>`
    + `<span class="ai-choice__text"><strong>${r.label}</strong></span></button>`).join('');

  $('ai-refine-reasons').querySelectorAll<HTMLElement>('.ai-choice').forEach((c) => {
    c.addEventListener('click', () => {
      refineReason = c.dataset.v ?? null;
      $('ai-refine-reasons').querySelectorAll<HTMLElement>('.ai-choice').forEach((x) => {
        const sel = x === c;
        x.classList.toggle('is-selected', sel);
        const radio = x.querySelector('.ai-choice__radio');
        if (radio) radio.innerHTML = sel ? '<span class="ai-choice__dot"></span>' : '';
      });
      // 「方向錯誤」不需要挑個別課程
      $('ai-refine-course-sec').style.display = refineReason === 'direction' ? 'none' : '';
    });
  });

  $('ai-refine-courses').innerHTML = (S.plan.courses ?? []).map((c) =>
    `<label class="ai-modal__course"><input type="checkbox" value="${c.id}">`
    + `<span>${esc(c.name)}<small>${c.credits} 學分 · ${esc(c.teacherName ?? '')}</small></span></label>`)
    .join('');
  $('ai-refine-course-sec').style.display = '';
  $('ai-refine-tries').textContent = `剩餘微調次數:${S.retriesLeft} 次`;
  $('ai-refine-modal').hidden = false;
}

const closeRefineModal = () => { $('ai-refine-modal').hidden = true; };

function submitRefine(): void {
  if (!refineReason) {
    window.alert('請先選擇微調原因');
    return;
  }
  const note = ($('ai-refine-note') as HTMLTextAreaElement).value.trim();
  const reasonLabel = REFINE_REASONS.find((r) => r.v === refineReason)?.label ?? '';
  closeRefineModal();

  if (refineReason === 'direction') {
    // 方向錯誤/大改:帶歷史 Context 回導師對話,重新收斂並覆寫意圖 JSON
    S.refineAfterChat = true;
    S.intentDone = false;
    ($('ai-to-plan') as HTMLButtonElement).disabled = true;
    gotoStep(2);
    const msg = `我看了推薦課表,覺得整體方向不太對${note ? `:${note}` : ''}。請重新跟我確認方向。`;
    addBubble('user', esc(msg));
    S.sending = true;
    void chatRequest(msg);
    return;
  }

  // 微調特定課程:不重開對話,黑名單 + 直接重排
  const picked: number[] = [];
  $('ai-refine-courses').querySelectorAll<HTMLInputElement>('input:checked')
    .forEach((i) => picked.push(Number(i.value)));
  for (const id of picked) {
    if (!S.blacklist.includes(id)) S.blacklist.push(id);
  }
  const names = (S.plan?.courses ?? []).filter((c) => picked.includes(c.id))
    .map((c) => `「${c.name}」`).join('、');
  const refineNote = `微調原因:${reasonLabel}`
    + (names ? `;請更換這些課程:${names}` : '')
    + (note ? `;補充需求:${note}` : '');
  void runPlan({ refine: true, refineNote });
}

// ══ 初始化 ════════════════════════════════════════════
async function initContext(): Promise<void> {
  try {
    S.ctx = await request<AdvisorContext>('/api/ai/advisor/context');
    S.retriesLeft = S.ctx.retriesLeft;
    // 頁首學分進度改吃真實資料(原本為硬編碼)
    const right = document.querySelector<HTMLElement>('.ekm-page-header__right');
    if (right && S.ctx.profile) {
      right.innerHTML =
        `<span>目前學分進度:<strong class="ic-6c61e1">${S.ctx.earned}</strong> / ${S.ctx.gradCredits}</span>`
        + '<span class="ekm-page-header__line"></span>'
        + `<span class="ekm-page-header__name">學號: ${esc(S.ctx.profile.account)} ${esc(S.ctx.profile.name)}</span>`;
    }
    renderWeakTags(); // 背景資料回來後重繪弱項標籤
  } catch { /* 401 會被 api.ts 導回登入頁 */ }
}

/** 初始化 AI 選課頁;頁面沒有 #ai-step-1 時不做事 */
export function initAdvisor(): void {
  if (!document.getElementById('ai-step-1')) return;

  loadSettings();
  renderForm();
  bindSlider();
  void initContext();

  // 步驟 1 → 2
  $('ai-to-chat').addEventListener('click', () => {
    gotoStep(2);
    void startChat();
  });

  // 步驟 2 事件
  $('ai-send').addEventListener('click', () => void sendChat());
  $('ai-input').addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') void sendChat();
  });
  $('ai-chat-restart').addEventListener('click', () => {
    S.chatStarted = false;
    void startChat();
  });
  $('ai-back-1').addEventListener('click', () => gotoStep(1));
  $('ai-to-plan').addEventListener('click', () => {
    const refine = S.refineAfterChat;
    S.refineAfterChat = false;
    void runPlan({ refine, refineNote: refine ? '方向已重新收斂,請依新意圖重排' : '' });
  });
  $('ai-skip-chat').addEventListener('click', () => {
    S.intentDone = false; // 略過對話 → 不帶意圖,純表單排課
    void runPlan({});
  });

  // 步驟 3 事件
  $('ai-adopt').addEventListener('click', () => void adoptPlan());
  $('ai-refine').addEventListener('click', openRefineModal);
  $('ai-restart-all').addEventListener('click', () => {
    S.blacklist = [];
    S.chatStarted = false;
    gotoStep(1);
  });

  // 微調彈窗事件
  $('ai-refine-cancel').addEventListener('click', closeRefineModal);
  $('ai-refine-submit').addEventListener('click', submitRefine);
  $('ai-refine-modal').addEventListener('click', (e) => {
    if (e.target === $('ai-refine-modal')) closeRefineModal();
  });
}
